import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Button, Paper, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Alert, CircularProgress, Divider,
  Tooltip, Chip, Autocomplete, InputAdornment, Collapse,
  ToggleButton, ToggleButtonGroup, useTheme
} from '@mui/material';
import { Add, Delete, ArrowBack, History, Save, Calculate, TuneOutlined } from '@mui/icons-material';
import { Product, MarketParams, Offer, CustomerContact } from '../../types';
import { getProducts } from '../../api/products';
import { getOffer, updateOffer } from '../../api/offers';
import { getContacts } from '../../api/customers';
import { getMarketParams } from '../../api/marketParams';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/axios';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';

const SectionTitle: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = BRAND }) => (
  <Typography sx={{
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color, mb: 1.5, mt: 0.5
  }}>{children}</Typography>
);

const statusStyle: Record<string, { bg: string; color: string }> = {
  DRAFT:    { bg: '#fff7ed', color: '#c2410c' },
  SENT:     { bg: '#eff6ff', color: '#1d4ed8' },
  ACCEPTED: { bg: '#f0fdf4', color: '#15803d' },
  REJECTED: { bg: '#fff0f0', color: '#dc2626' },
};
const statusLabel: Record<string, string> = { DRAFT: 'Taslak', SENT: 'Gönderildi', ACCEPTED: 'Kabul', REJECTED: 'Red' };

interface ItemRow {
  productId: number;
  productName: string;
  unit: string;
  unitWeight: number;
  alFactor: number;
  costPrice: number;
  quantity: number;
  unitPrice: number;
  profitMargin: number;
  total: number;
  autoPrice: number;
  autoPriceUsd: number;
  adet: number;
}

interface OfferParams {
  lmeAl: number;
  usdTry: number;
  eurTry: number;
  finans: number;
  nakliye: number;
  diger: number;
  vergiKad: number;
}

const EditOfferPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const { id } = useParams<{ id: string }>();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [marketParams, setMarketParams] = useState<MarketParams | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const navigate = useNavigate();

  const [form, setForm] = useState({
    deliveryLocation: 'EXW',
    downPaymentRate: 30,
    paymentDays: 0,
    currency: 'USD',
    status: 'DRAFT',
    notes: '',
    language: 'EN',
  });

  const [offerParams, setOfferParams] = useState<OfferParams>({
    lmeAl: 0, usdTry: 0, eurTry: 0, finans: 0, nakliye: 0, diger: 0, vergiKad: 0,
  });
  const offerParamsRef = useRef<OfferParams>(offerParams);
  const [showParamsEdit, setShowParamsEdit] = useState(false);

  const [nakliyeMode, setNakliyeMode] = useState<'percent' | 'amount'>('percent');
  const [nakliyeAmount, setNakliyeAmount] = useState(0);
  const [termsForm, setTermsForm] = useState({ paymentTerms: '', deliveryTime: '', deliveryPlace: '', optionTerms: '' });

  const [items, setItems] = useState<ItemRow[]>([]);
  const [competitorPrices, setCompetitorPrices] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [prevCurrency, setPrevCurrency] = useState('');
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');

  useEffect(() => {
    if (!id) return;
    Promise.all([
      getOffer(parseInt(id)),
      getProducts(),
      getMarketParams(),
      api.get('/competitor-prices').then(r => r.data).catch(() => [])
    ])
      .then(([o, p, m, cp]: any[]) => {
        setOffer(o);
        setProducts(p);
        setMarketParams(m);
        setCompetitorPrices(cp);
        setPrevCurrency(o.currency || 'USD');
        setForm({
          deliveryLocation: o.deliveryLocation || 'EXW',
          downPaymentRate: Math.round((o.downPaymentRate || 0.3) * 100),
          paymentDays: o.paymentDays || 0,
          currency: o.currency || 'USD',
          status: o.status || 'DRAFT',
          notes: o.notes || '',
          language: o.language || 'EN',
        });
        const initParams = {
          lmeAl:    o.lmeAlSnapshot    != null ? o.lmeAlSnapshot    : (m.lmeAl    || 0),
          usdTry:   o.usdTrySnapshot   != null ? o.usdTrySnapshot   : (m.usdTry   || 0),
          eurTry:   o.eurTrySnapshot   != null ? o.eurTrySnapshot   : (m.eurTry   || 0),
          finans:   o.finansSnapshot   != null ? o.finansSnapshot   : (m.finans   || 0),
          nakliye:  o.nakliyeSnapshot  != null ? o.nakliyeSnapshot  : (m.nakliye  || 0),
          diger:    o.digerSnapshot    != null ? o.digerSnapshot    : (m.diger    || 0),
          vergiKad: o.vergiKadSnapshot != null ? o.vergiKadSnapshot : (m.vergiKad || 0),
        };
        offerParamsRef.current = initParams;
        setOfferParams(initParams);
        if (o.shippingAmount && o.shippingAmount > 0) {
          setNakliyeMode('amount');
          setNakliyeAmount(o.shippingAmount);
        }
        setTermsForm({
          paymentTerms: o.paymentTerms || '',
          deliveryTime: o.deliveryTime || '',
          deliveryPlace: o.deliveryPlace || '',
          optionTerms: o.optionTerms || '',
        });
        if (o.customerId) {
          getContacts(o.customerId)
              .then(list => {
                setContacts(list);
                setSelectedContactId(o.contactId ? String(o.contactId) : (list.length > 0 ? String(list[0].id) : ''));
              })
              .catch(() => {});
        }
        setItems(o.items?.map((item: any) => {
          const profitMargin = Math.round((item.profitMargin || 0) * 100);
          const unitPrice = item.unitPrice;
          const autoPrice = profitMargin > 0
            ? parseFloat((unitPrice / (1 + profitMargin / 100)).toFixed(6))
            : unitPrice;
          return {
            productId: item.productId, productName: item.productName,
            unit: item.productUnit, unitWeight: item.unitWeight || 0,
            alFactor: 0, costPrice: 0, quantity: item.quantity,
            unitPrice, profitMargin, total: item.totalPrice, autoPrice, autoPriceUsd: 0, adet: 1,
          };
        }) || []);
      })
      .catch(() => setError('Veriler yüklenemedi'))
      .finally(() => setLoading(false));
  }, [id]);

  const convertFromUsd = (priceUsd: number, currency: string, params: OfferParams): number => {
    if (currency === 'TRY') return parseFloat((priceUsd * params.usdTry).toFixed(2));
    if (currency === 'EUR') return parseFloat((priceUsd / (params.eurTry / params.usdTry)).toFixed(3));
    return parseFloat(priceUsd.toFixed(3));
  };

  const calcAutoPrice = (product: Product, params: OfferParams, currency: string): number => {
    const alPrim = marketParams?.alPrim || 0;
    const vergiOrani = params.vergiKad === 0 ? 0.0 : 0.075;
    const alasim = (params.lmeAl + alPrim) * (1 + vergiOrani) + 650;
    const extras = 1 + params.finans / 100 + params.nakliye / 100 + params.diger / 100;
    let priceUsd: number;
    if (product.cableFamily === 'Compacted' || product.cableFamily === 'AAC-ACSR') {
      priceUsd = (product.costPrice || 0) * extras;
    } else if (product.unit === 'KG') {
      priceUsd = (alasim * product.aluminumFactor * extras) / 1000 + (product.costPrice || 0);
    } else {
      priceUsd = (product.costPrice || 0) * extras;
    }
    if (currency === 'EUR') return parseFloat((priceUsd / (params.eurTry / params.usdTry)).toFixed(3));
    if (currency === 'TRY') return parseFloat((priceUsd * params.usdTry).toFixed(2));
    return parseFloat(priceUsd.toFixed(3));
  };

  useEffect(() => {
    if (items.length === 0 || !marketParams || !prevCurrency || prevCurrency === form.currency) return;
    const toUsd = (price: number, currency: string): number => {
      if (currency === 'TRY') return price / marketParams.usdTry;
      if (currency === 'EUR') return price * (marketParams.eurTry / marketParams.usdTry);
      return price;
    };
    const fromUsd = (usdPrice: number, currency: string): number => {
      if (currency === 'TRY') return Math.round(usdPrice * marketParams.usdTry * 1000) / 1000;
      if (currency === 'EUR') return Math.round(usdPrice / (marketParams.eurTry / marketParams.usdTry) * 1000) / 1000;
      return Math.round(usdPrice * 1000) / 1000;
    };
    setItems(prev => prev.map(item => {
      const newUnitPrice = fromUsd(toUsd(item.unitPrice, prevCurrency), form.currency);
      const newAutoPrice = fromUsd(toUsd(item.autoPrice, prevCurrency), form.currency);
      return { ...item, autoPrice: newAutoPrice, unitPrice: newUnitPrice, total: Math.round(item.adet * item.quantity * newUnitPrice * 100) / 100 };
    }));
    if (nakliyeMode === 'amount' && nakliyeAmount > 0) {
      const nakliyeUsd = toUsd(nakliyeAmount, prevCurrency);
      setNakliyeAmount(parseFloat(fromUsd(nakliyeUsd, form.currency).toFixed(2)));
    }
    setPrevCurrency(form.currency);
  }, [form.currency]); // eslint-disable-line

  const recalcAll = async (params?: OfferParams) => {
    if (items.length === 0) return;
    const currentParams = params || offerParamsRef.current;
    const productIds = Array.from(new Set(items.map(item => item.productId)));
    let priceMap = new Map<number, number>();
    try {
      const response = await api.post('/cost/calculate-offer-prices', {
        productIds, lmeAl: currentParams.lmeAl, usdTry: currentParams.usdTry, eurTry: currentParams.eurTry,
        finans: currentParams.finans, nakliye: currentParams.nakliye, diger: currentParams.diger, vergiKad: currentParams.vergiKad,
      });
      response.data.results.forEach((r: any) => priceMap.set(r.productId, r.autoPrice));
    } catch (_) { /* fallback to calcAutoPrice below */ }

    const extMult = 1 + (currentParams.finans + currentParams.nakliye + currentParams.diger) / 100;

    setItems(prev => prev.map(item => {
      const autoPriceUsd = priceMap.get(item.productId) ?? item.autoPriceUsd;
      const autoPrice = autoPriceUsd > 0
        ? convertFromUsd(autoPriceUsd * extMult, form.currency, currentParams)
        : (() => { const product = products.find(p => p.id === item.productId); return product ? calcAutoPrice(product, currentParams, form.currency) : item.autoPrice; })();
      const unitPrice = parseFloat((autoPrice * (1 + item.profitMargin / 100)).toFixed(3));
      return { ...item, autoPriceUsd: autoPriceUsd || item.autoPriceUsd, autoPrice, unitPrice, total: item.adet * item.quantity * unitPrice };
    }));
  };

  const addItem = async () => {
    if (!selectedProduct) return;
    const product = products.find(p => p.id === parseInt(selectedProduct));
    if (!product) return;
    const p = offerParamsRef.current;
    let autoPriceUsd = 0;
    try {
      const response = await api.post('/cost/calculate-offer-prices', {
        productIds: [product.id], lmeAl: p.lmeAl, usdTry: p.usdTry, eurTry: p.eurTry,
        finans: p.finans, nakliye: p.nakliye, diger: p.diger, vergiKad: p.vergiKad,
      });
      autoPriceUsd = response.data.results?.[0]?.autoPrice ?? 0;
    } catch (_) { /* fallback */ }
    const qty = product.unit === 'KG' ? 1000 : 500;
    const extMult = 1 + (p.finans + p.nakliye + p.diger) / 100;
    const autoPrice = autoPriceUsd > 0
      ? convertFromUsd(autoPriceUsd * extMult, form.currency, p)
      : calcAutoPrice(product, p, form.currency);
    const unitPrice = parseFloat((autoPrice * 1.1).toFixed(3));
    setItems([...items, {
      productId: product.id, productName: product.name,
      unit: product.unit, unitWeight: product.unitWeight || 0,
      alFactor: product.aluminumFactor || 0, costPrice: product.costPrice || 0,
      quantity: qty, unitPrice, profitMargin: 10,
      total: qty * unitPrice, autoPrice, autoPriceUsd, adet: 1,
    }]);
    setSelectedProduct('');
  };

  const updateItem = (idx: number, field: keyof ItemRow, value: number) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'profitMargin') {
        updated.unitPrice = parseFloat((item.autoPrice * (1 + value / 100)).toFixed(3));
      } else if (field === 'unitPrice' && item.autoPrice > 0) {
        updated.profitMargin = parseFloat(((value / item.autoPrice - 1) * 100).toFixed(1));
      }
      updated.total = updated.adet * updated.quantity * updated.unitPrice;
      return updated;
    }));
  };

  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  // Rakip karşılaştırma helper
  const getCompetitorComparison = (item: ItemRow) => {
    const product = products.find(p => p.id === item.productId);
    if (!product) return null;
    const specMatch = product.name.match(/(\d+x\d+)/);
    if (!specMatch) return null;
    const spec = specMatch[1];
    const familyMatch = product.name.match(/^([A-Z0-9]+)/);
    if (!familyMatch) return null;
    const family = familyMatch[1];

    const matches = competitorPrices.filter(
      (cp: any) => cp.cableFamily === family && cp.spec === spec && cp.competitorName !== 'KLC'
    );
    if (matches.length === 0) return null;

    const usdTry = marketParams?.usdTry || 0;
    const eurTry = marketParams?.eurTry || 0;
    let bizTl = item.unitPrice;
    if (form.currency === 'USD') bizTl = item.unitPrice * usdTry;
    else if (form.currency === 'EUR') bizTl = item.unitPrice * eurTry;

    return matches.map((m: any): any => {
      if (!m.listPriceTl || bizTl <= 0) return { name: m.competitorName, status: 'no-data' };
      if (bizTl > m.listPriceTl) {
        const overPct = ((bizTl - m.listPriceTl) / m.listPriceTl) * 100;
        return { name: m.competitorName, status: 'more-expensive', listPrice: m.listPriceTl, overPct: overPct.toFixed(1) };
      }
      const requiredDiscount = ((m.listPriceTl - bizTl) / m.listPriceTl) * 100;
      return { name: m.competitorName, status: 'need-discount', listPrice: m.listPriceTl, requiredDiscount: requiredDiscount.toFixed(1) };
    });
  };

  const allCompetitors = Array.from(new Set(
    competitorPrices.filter((cp: any) => cp.competitorName !== 'KLC').map((cp: any) => cp.competitorName)
  ));

  // Ton ve TIR hesapları
  const totalKg = items.reduce((sum, item) => {
    const kgPerUnit = item.unit === 'KG' ? 1 : (item.unitWeight || 0);
    return sum + item.adet * item.quantity * kgPerUnit;
  }, 0);
  const totalTon = totalKg / 1000;
  const tirCount = Math.ceil(totalTon / 20);

  const itemsTotal = items.reduce((sum, i) => sum + i.total, 0);
  const nakliyeTotal = nakliyeMode === 'amount' ? nakliyeAmount : 0;
  const grandTotal = itemsTotal + nakliyeTotal;

  const currencySym = form.currency === 'TRY' ? '₺' : form.currency === 'EUR' ? '€' : '$';

  const handleSave = async () => {
    if (!id || items.length === 0) { setError('En az bir ürün ekleyiniz'); return; }
    setSaving(true);
    try {
      await updateOffer(parseInt(id), {
        contactId: selectedContactId ? parseInt(selectedContactId) : undefined,
        deliveryLocation: form.deliveryLocation,
        downPaymentRate: form.downPaymentRate / 100,
        paymentDays: form.paymentDays,
        currency: form.currency,
        status: form.status,
        notes: form.notes || undefined,
        language: form.language,
        revisionNote: revisionNote || undefined,
        shippingAmount: nakliyeMode === 'amount' && nakliyeAmount > 0 ? nakliyeAmount : undefined,
        paymentTerms: termsForm.paymentTerms || undefined,
        deliveryTime: termsForm.deliveryTime || undefined,
        deliveryPlace: termsForm.deliveryPlace || undefined,
        optionTerms: termsForm.optionTerms || undefined,
        snapshotLmeAl: offerParamsRef.current.lmeAl,
        snapshotAlPrim: marketParams?.alPrim,
        snapshotUsdTry: offerParamsRef.current.usdTry,
        snapshotEurTry: offerParamsRef.current.eurTry,
        snapshotFinans: offerParamsRef.current.finans,
        snapshotNakliye: offerParamsRef.current.nakliye,
        snapshotDiger: offerParamsRef.current.diger,
        snapshotVergiKad: offerParamsRef.current.vergiKad,
        items: items.map(i => ({
          productId: i.productId, quantity: i.adet * i.quantity,
          pcs: i.adet, unitPrice: i.unitPrice, profitMargin: i.profitMargin / 100,
        }))
      });
      navigate(`/offers/${id}`);
    } catch { setError('Kayıt sırasında hata oluştu'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress sx={{ color: BRAND }} /></Box>;

  const sStyle = statusStyle[form.status] || { bg: '#f4f5f7', color: '#6b7280' };

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(`/offers/${id}`)}
                  sx={{ color: TEXT2, textTransform: 'none', fontSize: '13px', mt: 0.5 }}>
            Geri
          </Button>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                Teklif Düzenle
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: BRAND, fontFamily: 'monospace' }}>
                — {offer?.orderNo}
              </Typography>
              <Chip label={statusLabel[form.status] || form.status} size="small"
                    sx={{ height: 22, fontSize: '11px', fontWeight: 700,
                      bgcolor: sStyle.bg, color: sStyle.color, borderRadius: '20px' }} />
            </Box>
            <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
              Değişiklikler otomatik arşivlenir
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate(`/offers/${id}`)}
                  sx={{ borderColor: BORDER, color: TEXT2, textTransform: 'none',
                    fontSize: '13px', borderRadius: '8px', height: 38, px: 2.5 }}>
            İptal
          </Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                  onClick={handleSave} disabled={saving}
                  sx={{ bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                    px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                    '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
            Kaydet
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Teklif Bilgileri ── */}
      <Paper elevation={0} sx={{ p: 3, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <SectionTitle>Teklif Bilgileri</SectionTitle>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap',
                   '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          {contacts.length > 0 && (
              <TextField select label="İlgili Kişi" value={selectedContactId} size="small"
                         onChange={e => setSelectedContactId(e.target.value)} sx={{ minWidth: 200 }}>
                {contacts.map(ct => (
                    <MenuItem key={ct.id} value={String(ct.id)}>{ct.name}{ct.title ? ` — ${ct.title}` : ''}</MenuItem>
                ))}
              </TextField>
          )}
          <TextField select label="Para Birimi" value={form.currency} size="small"
                     onChange={(e) => setForm({ ...form, currency: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value="USD">🇺🇸 USD</MenuItem>
            <MenuItem value="EUR">🇪🇺 EUR</MenuItem>
            <MenuItem value="TRY">🇹🇷 TRY</MenuItem>
          </TextField>
          <TextField select label="Teslim Yeri" value={form.deliveryLocation} size="small"
                     onChange={(e) => setForm({ ...form, deliveryLocation: e.target.value })} sx={{ minWidth: 130 }}>
            {['EXW','FOB','CIF','DDP'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
          <TextField label="Ön Ödeme" type="number" value={form.downPaymentRate} size="small"
                     onChange={(e) => setForm({ ...form, downPaymentRate: parseInt(e.target.value) })}
                     sx={{ minWidth: 130 }}
                     InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
          <TextField label="Vade" type="number" value={form.paymentDays} size="small"
                     onChange={(e) => setForm({ ...form, paymentDays: parseInt(e.target.value) })}
                     sx={{ minWidth: 130 }}
                     InputProps={{ endAdornment: <InputAdornment position="end">gün</InputAdornment> }} />
          <TextField select label="Durum" value={form.status} size="small"
                     onChange={(e) => setForm({ ...form, status: e.target.value })} sx={{ minWidth: 150 }}>
            <MenuItem value="DRAFT">Taslak</MenuItem>
            <MenuItem value="SENT">Gönderildi</MenuItem>
            <MenuItem value="ACCEPTED">Kabul Edildi</MenuItem>
            <MenuItem value="REJECTED">Reddedildi</MenuItem>
          </TextField>
          <TextField select label="PDF Dili" value={form.language} size="small"
                     onChange={(e) => setForm({ ...form, language: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value="EN">🇬🇧 English</MenuItem>
            <MenuItem value="TR">🇹🇷 Türkçe</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField label="Teklif Notu (PDF'de görünür)" multiline rows={2} fullWidth size="small" value={form.notes}
                     onChange={(e) => setForm({ ...form, notes: e.target.value })}
                     placeholder="Terms & Conditions altında PDF'e eklenir — opsiyonel"
                     inputProps={{ maxLength: 500 }}
                     sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                     InputProps={{ endAdornment: <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 1 }}>
                       <Typography sx={{ fontSize: '11px', color: TEXT2 }}>{form.notes.length}/500</Typography>
                     </InputAdornment> }} />
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <SectionTitle color={ACCENT}>PDF Koşullar (boş bırakılırsa PDF'de gösterilmez)</SectionTitle>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap',
                     '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
            <TextField label="Ödeme Şekli / Payment Terms" multiline rows={2} size="small" value={termsForm.paymentTerms}
                       onChange={e => setTermsForm({ ...termsForm, paymentTerms: e.target.value })}
                       sx={{ flex: '1 1 300px' }} placeholder="örn: %30 peşin, %70 yükleme öncesi..." />
            <TextField label="Teslim Süresi / Delivery Time" multiline rows={2} size="small" value={termsForm.deliveryTime}
                       onChange={e => setTermsForm({ ...termsForm, deliveryTime: e.target.value })}
                       sx={{ flex: '1 1 300px' }} placeholder="örn: Sipariş onayından itibaren 6-8 hafta..." />
            <TextField label="Teslim Yeri / Delivery Place" multiline rows={2} size="small" value={termsForm.deliveryPlace}
                       onChange={e => setTermsForm({ ...termsForm, deliveryPlace: e.target.value })}
                       sx={{ flex: '1 1 300px' }} placeholder="örn: KLC Kablo A.Ş. Fabrika çıkışı..." />
            <TextField label="Opsiyon / Option" multiline rows={2} size="small" value={termsForm.optionTerms}
                       onChange={e => setTermsForm({ ...termsForm, optionTerms: e.target.value })}
                       sx={{ flex: '1 1 300px' }} placeholder="örn: Teklif 15 gün geçerlidir..." />
          </Box>
        </Box>
      </Paper>

      {/* ── Piyasa Parametreleri ── */}
      <Paper elevation={0} sx={{ mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{ p: 2, bgcolor: BRAND, display: 'flex', alignItems: 'center',
                   gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { label: 'LME AL', val: `${offerParams.lmeAl} $/t` },
                { label: 'USD/TRY', val: `₺${offerParams.usdTry}` },
                { label: 'EUR/TRY', val: `₺${offerParams.eurTry}` },
                { label: 'Finans', val: `%${offerParams.finans}` },
                { label: 'Nakliye', val: nakliyeMode === 'percent' ? `%${offerParams.nakliye}` : `${currencySym}${nakliyeAmount.toLocaleString()}` },
                { label: 'Diğer', val: `%${offerParams.diger}` },
              ].map(({ label, val }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255,255,255,0.65)',
                                     letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.3 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'white', lineHeight: 1.3 }}>
                    {val}
                  </Typography>
                </Box>
              ))}
            </Box>
            {marketParams && (
              <Box sx={{ mt: 1.5, pt: 1.2, borderTop: '1px solid rgba(255,255,255,0.25)',
                         display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ bgcolor: '#FCD34D', borderRadius: '5px', px: 1, py: 0.4 }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#78350F',
                                     letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1 }}>
                    Güncel Piyasa
                  </Typography>
                </Box>
                {[
                  { label: 'LME AL', val: `${marketParams.lmeAl} $/t` },
                  { label: 'Al Prim', val: `${marketParams.alPrim} $/t` },
                  { label: 'USD/TRY', val: `₺${marketParams.usdTry}` },
                  { label: 'EUR/TRY', val: `₺${marketParams.eurTry}` },
                  { label: 'Finans', val: `%${marketParams.finans}` },
                  { label: 'Nakliye', val: `%${marketParams.nakliye}` },
                  { label: 'Diğer', val: `%${marketParams.diger}` },
                  { label: 'Vergi', val: marketParams.vergiKad === 0 ? '%0' : '%7.5' },
                ].map(({ label, val }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                    bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '5px', px: 1, py: 0.4 }}>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.65)' }}>{label}:</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{val}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Button size="small" startIcon={<TuneOutlined sx={{ fontSize: 16 }} />} onClick={() => setShowParamsEdit(v => !v)}
                  sx={{ minWidth: 110, height: 32, color: 'white', fontSize: '12px', fontWeight: 600,
                    textTransform: 'none', borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    bgcolor: showParamsEdit ? ACCENT : 'transparent',
                    borderColor: showParamsEdit ? ACCENT : 'rgba(255,255,255,0.3)',
                    '&:hover': { bgcolor: showParamsEdit ? '#a93226' : 'rgba(255,255,255,0.1)',
                      borderColor: showParamsEdit ? '#a93226' : 'white' } }}>
            {showParamsEdit ? 'Kapat' : 'Düzenle'}
          </Button>
        </Box>
        <Collapse in={showParamsEdit}>
          <Box sx={{ p: 2.5, bgcolor: '#f8fbff', borderTop: `1px solid ${BORDER}`,
                     display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end',
                     '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
            {[
              { label: 'LME AL', key: 'lmeAl', end: '$/ton', width: 160 },
              { label: 'USD/TRY', key: 'usdTry', end: '₺', width: 140 },
              { label: 'EUR/TRY', key: 'eurTry', end: '₺', width: 140 },
              { label: 'Finans', key: 'finans', end: '%', width: 110 },
              { label: 'Diğer', key: 'diger', end: '%', width: 110 },
            ].map(({ label, key, end, width }) => (
              <TextField key={key} label={label} type="number" size="small"
                         value={(offerParams as any)[key]}
                         onChange={(e) => { const v = { ...offerParams, [key]: parseFloat(e.target.value) || 0 }; offerParamsRef.current = v; setOfferParams(v); }}
                         slotProps={{ input: { endAdornment: <InputAdornment position="end">{end}</InputAdornment> } }}
                         sx={{ width }} />
            ))}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: TEXT2,
                                 letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Nakliye
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ToggleButtonGroup value={nakliyeMode} exclusive size="small"
                                   onChange={(_, v) => { if (v) setNakliyeMode(v); }}>
                  <ToggleButton value="percent" sx={{ fontSize: '11px', px: 1.5, borderRadius: '8px 0 0 8px' }}>%</ToggleButton>
                  <ToggleButton value="amount" sx={{ fontSize: '11px', px: 1.5, borderRadius: '0 8px 8px 0' }}>{currencySym}</ToggleButton>
                </ToggleButtonGroup>
                {nakliyeMode === 'percent' ? (
                  <TextField type="number" size="small" value={offerParams.nakliye}
                             onChange={(e) => { const v = { ...offerParams, nakliye: parseFloat(e.target.value) || 0 }; offerParamsRef.current = v; setOfferParams(v); }}
                             slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                             sx={{ width: 100 }} />
                ) : (
                  <TextField type="number" size="small" value={nakliyeAmount}
                             onChange={(e) => setNakliyeAmount(parseFloat(e.target.value) || 0)}
                             slotProps={{ input: { endAdornment: <InputAdornment position="end">{currencySym}</InputAdornment> } }}
                             sx={{ width: 130 }} />
                )}
              </Box>
            </Box>
            <TextField select label="Vergi" size="small" value={offerParams.vergiKad}
                       onChange={(e) => { const v = { ...offerParams, vergiKad: parseInt(e.target.value) }; offerParamsRef.current = v; setOfferParams(v); }}
                       sx={{ width: 150 }}>
              <MenuItem value={0}>Vergisiz %0</MenuItem>
              <MenuItem value={2}>Vergili %7.5</MenuItem>
            </TextField>
            <Button variant="contained" startIcon={<Calculate sx={{ fontSize: 16 }} />} size="small"
                    onClick={() => recalcAll()} disabled={items.length === 0}
                    sx={{ height: 40, alignSelf: 'flex-end', bgcolor: BRAND, borderRadius: '8px',
                      textTransform: 'none', fontSize: '12px', fontWeight: 600, boxShadow: 'none',
                      '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
              Fiyatları Güncelle
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* ── Revizyon Notu ── */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: `1px solid #dbeafe`,
                                  bgcolor: '#f8fbff', borderRadius: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box sx={{ width: 32, height: 32, bgcolor: '#e3f2fd', borderRadius: '8px',
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <History sx={{ color: BRAND, fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: BRAND, lineHeight: 1.2 }}>
              Revizyon Notu
            </Typography>
            <Typography sx={{ fontSize: '11px', color: TEXT2 }}>
              Bu teklifi kaydedince mevcut hali otomatik arşivlenir (R1, R2...)
            </Typography>
          </Box>
          <Chip label="Opsiyonel" size="small"
                sx={{ height: 20, bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '10px', fontWeight: 600 }} />
        </Box>
        <TextField fullWidth multiline rows={2} size="small"
                   placeholder="Örn: Fiyat güncellemesi yapıldı..."
                   value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)}
                   inputProps={{ maxLength: 300 }}
                   sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                   InputProps={{ endAdornment: <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 1 }}>
                     <Typography sx={{ fontSize: '11px', color: TEXT2 }}>{revisionNote.length}/300</Typography>
                   </InputAdornment> }} />
      </Paper>

      {/* ── Ürünler ── */}
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <SectionTitle>Ürünler</SectionTitle>
          {items.length > 0 && (
            <Chip label={`${items.length} ürün`} size="small"
                  sx={{ height: 22, bgcolor: BRAND, color: 'white', fontWeight: 700,
                    fontSize: '11px', borderRadius: '6px', mt: -1.2 }} />
          )}
          {totalTon > 0 && (
            <Chip label={`${totalTon.toFixed(2)} ton · ~${tirCount} TIR`} size="small"
                  sx={{ height: 22, bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700,
                    fontSize: '11px', borderRadius: '6px', mt: -1.2 }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3,
                   '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          <Autocomplete options={products} getOptionLabel={(p) => `${p.name} (${p.cableFamily})`}
                        value={products.find(p => p.id === parseInt(selectedProduct)) || null}
                        onChange={(_, newVal) => setSelectedProduct(newVal ? String(newVal.id) : '')}
                        size="small"
                        renderInput={(params) => <TextField {...params} label="Ürün Ara / Seç" placeholder="İsim veya kablo ailesi..." />}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                              <Box>
                                <Box sx={{ fontWeight: 600, fontSize: '13px' }}>{option.name}</Box>
                                <Box sx={{ fontSize: '11px', color: TEXT2 }}>{option.cableFamily} · {option.unit}</Box>
                              </Box>
                              <Box sx={{ fontSize: '12px', color: '#16A34A', fontWeight: 600, flexShrink: 0 }}>
                                {calcAutoPrice(option, offerParamsRef.current, form.currency).toFixed(3)} {form.currency}
                              </Box>
                            </Box>
                          </li>
                        )}
                        filterOptions={(options, { inputValue }) => options.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()) || o.cableFamily.toLowerCase().includes(inputValue.toLowerCase()))}
                        sx={{ minWidth: 380 }} noOptionsText="Ürün bulunamadı" />
          <Button variant="contained" startIcon={<Add />} onClick={addItem} disabled={!selectedProduct}
                  sx={{ bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                    px: 2.5, fontSize: '13px', boxShadow: 'none', height: 40,
                    '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
            Ekle
          </Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { label: 'Ürün', minWidth: 140 },
                { label: 'Birim', width: 60 },
                { label: 'Adet', width: 80, align: 'center' },
                { label: 'Miktar', width: 110, align: 'center' },
                { label: 'Ton', width: 80, align: 'right' },
                { label: 'Kâr (%)', width: 130, align: 'center' },
                { label: '', width: 36 },
                { label: `Birim Fiyat (${form.currency})`, width: 130, align: 'right' },
                { label: 'Toplam', width: 120, align: 'right' },
                { label: '', width: 40 },
              ].map((h, idx) => (
                <TableCell key={idx} align={(h.align as any) || 'left'}
                           style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                             fontSize: 11, fontWeight: 600, color: '#8a92a6',
                             textTransform: 'uppercase', letterSpacing: '0.05em',
                             borderBottom: '1px solid #e8eaf0',
                             minWidth: h.minWidth, width: h.width }}>
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 6, color: TEXT2 }}>
                  <Typography sx={{ fontSize: '13px' }}>Ürün seçip "Ekle" butonuna basın</Typography>
                </TableCell>
              </TableRow>
            ) : items.map((item, idx) => {
              const kgPerUnit = item.unit === 'KG' ? 1 : (item.unitWeight || 0);
              const rowTon = (item.adet * item.quantity * kgPerUnit) / 1000;
              return (
                <React.Fragment key={`${item.productId}-${idx}`}>
                  <TableRow sx={{
                    transition: 'background .15s',
                    '&:hover': { bgcolor: '#f0f7ff' },
                    '& > td': { borderBottom: 'none', pt: 1.2 },
                    '& > td:first-of-type': { borderLeft: `3px solid ${BRAND}` },
                  }}>
                    <TableCell>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: BRAND }}>
                        {item.productName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.unit} size="small" variant="outlined"
                            sx={{ fontSize: '11px', height: 20, borderRadius: '6px' }} />
                    </TableCell>
                    <TableCell align="center">
                      <TextField type="number" value={item.adet} size="small"
                                 onChange={(e) => { const v = parseInt(e.target.value); if (v >= 1) updateItem(idx, 'adet', v); }}
                                 inputProps={{ min: 1 }}
                                 sx={{ width: 70, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                    </TableCell>
                    <TableCell align="center">
                      <TextField type="number" value={item.quantity} size="small"
                                 onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value))}
                                 sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: '11px', color: TEXT2, fontWeight: 700 }}>
                        {rowTon.toFixed(2)}t
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField type="number" value={item.profitMargin} size="small"
                                 onChange={(e) => { const v = parseFloat(e.target.value); if (v >= 0) updateItem(idx, 'profitMargin', v); }}
                                 inputProps={{ min: 0, step: 0.5 }}
                                 InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                 sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                    </TableCell>
                    <TableCell align="left" sx={{ pl: 0.5, pr: 0 }}>
                      {idx === 0 && items.length > 1 && (
                        <Tooltip title="Bu kâr oranını tüm ürünlere uygula">
                          <IconButton size="small"
                                      onClick={() => {
                                        const margin = item.profitMargin;
                                        setItems(prev => prev.map((it, i) => {
                                          if (i === 0) return it;
                                          const unitPrice = parseFloat((it.autoPrice * (1 + margin / 100)).toFixed(3));
                                          return { ...it, profitMargin: margin, unitPrice, total: it.adet * it.quantity * unitPrice };
                                        }));
                                      }}
                                      sx={{ bgcolor: BRAND, color: 'white', width: 24, height: 24,
                                        borderRadius: '6px',
                                        '&:hover': { bgcolor: ACCENT } }}>
                            <span style={{ fontSize: '10px', fontWeight: 'bold' }}>↓↓</span>
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" value={item.unitPrice} size="small"
                                 onChange={(e) => updateItem(idx, 'unitPrice', parseFloat(e.target.value))}
                                 sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: BRAND }}>
                        {item.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Sil">
                        <IconButton size="small" onClick={() => removeItem(idx)}
                                    sx={{ width: 28, height: 28, borderRadius: '6px', color: '#64748b',
                                      '&:hover': { bgcolor: '#fff0f0', color: '#dc2626' } }}>
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Rakip Karşılaştırma */}
                  {(() => {
                    const comparison = getCompetitorComparison(item);
                    if (!allCompetitors.length) return null;
                    return (
                      <TableRow sx={{
                        bgcolor: '#F8FAFC',
                        '&:hover': { bgcolor: '#F8FAFC' },
                        '& > td': {
                          borderLeft: `3px solid ${BRAND}`,
                          borderBottom: '1px solid #CBD5E1',
                          pt: 0.5, pb: 1.2
                        }
                      }}>
                        <TableCell colSpan={10} sx={{ py: 0.8, px: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#64748B',
                                               letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              📊 Rakip Analizi
                            </Typography>
                            {allCompetitors.map(compName => {
                              const data: any = comparison?.find((c: any) => c.name === compName);
                              if (!data) {
                                return (
                                  <Chip key={compName} size="small" label={`${compName}: mevcut değil`}
                                        sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 'bold',
                                          textDecoration: 'line-through', fontSize: '11px', height: 22 }} />
                                );
                              }
                              if (data.status === 'more-expensive') {
                                return (
                                  <Chip key={compName} size="small"
                                        label={`${compName}: ${data.listPrice.toFixed(2)} TL → ⚠ biz %${data.overPct} pahalıyız`}
                                        sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 'bold', fontSize: '11px', height: 22 }} />
                                );
                              }
                              if (data.status === 'need-discount') {
                                const disc = parseFloat(data.requiredDiscount || '0');
                                if (disc < 0.5) {
                                  return (
                                    <Chip key={compName} size="small"
                                          label={`${compName}: ${data.listPrice.toFixed(2)} TL → ≈ aynı fiyat`}
                                          sx={{ bgcolor: '#E0E7FF', color: '#3730A3', fontWeight: 'bold', fontSize: '11px', height: 22 }} />
                                  );
                                }
                                return (
                                  <Chip key={compName} size="small"
                                        label={`${compName}: ${data.listPrice.toFixed(2)} TL → ✓ biz daha ucuzuz, %${data.requiredDiscount} iskonto eşitlenir`}
                                        sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 'bold', fontSize: '11px', height: 22 }} />
                                );
                              }
                              return null;
                            })}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                  <TableRow sx={{ height: 6, '& td': { border: 'none', p: 0 } }}><TableCell colSpan={10} /></TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>

        {items.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              {/* Ton / TIR özeti */}
              <Box sx={{ p: 2, bgcolor: '#FEF3C7', borderRadius: '10px', border: '1px solid #FDE68A' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#B45309',
                                   letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block' }}>
                  Toplam Ağırlık
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#B45309', lineHeight: 1.2 }}>
                  {totalTon.toFixed(3)} ton
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#B45309' }}>
                  ≈ {tirCount} TIR (20t/TIR)
                </Typography>
              </Box>

              {/* Genel toplam */}
              <Box sx={{ textAlign: 'right' }}>
                {nakliyeMode === 'amount' && nakliyeAmount > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: '11px', color: TEXT2 }}>Ürünler Toplamı</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                      {itemsTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: TEXT2, mt: 0.5 }}>Nakliye</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#B45309' }}>
                      + {nakliyeAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}
                    </Typography>
                    <Divider sx={{ my: 0.75 }} />
                  </Box>
                )}
                <Box sx={{ px: 3, py: 2, bgcolor: BRAND, borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255,255,255,0.7)',
                                     letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Genel Toplam
                  </Typography>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                    {grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {form.currency}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EditOfferPage;
